import { memo, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, CartContainer, EntityList } from '@aimsos/ui';

import NoteInsightTopic from './NoteInsightTopic.jsx';

import { Clock, FileText, Users } from 'lucide-react';

/** "Sep 2, 2026 · 2:05 PM" — the prototype writes a meeting record with its year and time. */
const formatWhen = (iso, language, t) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return t('history.notes.when', {
    date: date.toLocaleDateString(language, { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString(language, { hour: 'numeric', minute: '2-digit' }),
  });
};

/**
 * One meeting record on From notes (CT-5724), drawn as ONE card as in the
 * prototype: what it was and its type as a chip, how many claims in it are still
 * unconfirmed and "Meeting record" on the right, when (date and time) and from
 * which file — then, inside the same card, the readings the Concierge took from
 * it.
 */
const NoteInsightGroup = memo(function NoteInsightGroup({ insight }) {
  const { t, i18n } = useTranslation('unified-profiles');

  const header = useMemo(() => {
    const when = formatWhen(insight.occurredAt, i18n?.language, t);
    return {
      title: insight.title,
      icon: Users,
      iconHighlight: { bgColor: 'Neutral', iconColor: 'Neutral' },
      primaryMeta: insight.subtitle
        ? [
            {
              id: 'type',
              label: (
                <Badge variant="neutral" size="small">
                  <span data-testid="note-insight-type">{insight.subtitle}</span>
                </Badge>
              ),
            },
          ]
        : undefined,
      status: insight.unconfirmedClaims
        ? { label: t('history.notes.unconfirmedClaims', { count: insight.unconfirmedClaims }), variant: 'neutral' }
        : undefined,
      timestamp: insight.recordLabel ?? undefined,
      secondaryMeta: [
        when ? { id: 'when', icon: Clock, label: <span data-testid="note-insight-when">{when}</span> } : null,
        insight.file ? { id: 'file', icon: FileText, label: insight.file } : null,
      ].filter(Boolean),
    };
  }, [insight, i18n?.language, t]);

  return (
    <div data-testid="note-insight">
      <CartContainer size="S" variant="neutral">
        <EntityList {...header} />
        {insight.topics.map((topic) => (
          <NoteInsightTopic key={topic.id} topic={topic} />
        ))}
      </CartContainer>
    </div>
  );
});

export default NoteInsightGroup;
